import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCrm } from "@/context/CrmContext";
import { StageBadge, StatusBadge, ServiceTag } from "@/components/Badges";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES, STAGES, STAGE_LABELS, inr, fmtTime } from "@/lib/constants";
import { toast } from "sonner";

const Filter = ({ value, onChange, options, testId, all }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="h-10 w-full border-neutral-200 bg-white text-neutral-800 sm:w-48"><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Leads() {
  const { user, isManager } = useAuth();
  const { tick, refresh } = useCrm();
  const [stage, setStage] = useState("all");
  const [service, setService] = useState("all");
  const [q, setQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { q: q || undefined, stage: stage === "all" ? undefined : stage, service: service === "all" ? undefined : service };
      api.get("/leads", { params }).then((r) => setLeads(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [stage, service, q, tick]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: "saminfratech_leads.csv" }).click();
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold text-[#151515]">Leads</h1><p className="text-sm text-neutral-500">{isManager ? "All accounts across verticals" : "Your assigned accounts"} · {leads.length}</p></div>
        <div className="flex gap-2">
          {user.role === "ADMIN" && <button data-testid="export-leads-btn" onClick={exportCsv} className="flex h-10 items-center gap-1.5 rounded-lg border border-neutral-200 px-3 text-sm text-neutral-600"><Download className="h-4 w-4" /><span className="hidden sm:inline">Export</span></button>}
          <button data-testid="leads-add-btn" onClick={() => setOpen(true)} className="flex h-10 items-center gap-1.5 rounded-full bg-[#1F9E7A] px-4 text-sm font-semibold text-white"><Plus className="h-4 w-4" />New</button>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input data-testid="leads-filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="h-10 flex-1 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-[#1A1A1A] placeholder:text-neutral-400" />
        <Filter value={stage} onChange={setStage} testId="leads-filter-stage" all="All stages" options={STAGES.map((s) => [s, STAGE_LABELS[s]])} />
        <Filter value={service} onChange={setService} testId="leads-filter-service" all="All services" options={Object.entries(SERVICES)} />
      </div>
      <div className="overflow-hidden rounded-2xl border border-neutral-200">
        {leads.map((l) => (
          <Link key={l.id} to={`/leads/${l.id}`} data-testid={`leads-row-${l.id}`} className="grid grid-cols-1 gap-2 border-b border-neutral-200 bg-white px-4 py-3 transition-colors hover:bg-[#F6F8F5] md:grid-cols-12 md:items-center">
            <div className="md:col-span-3"><p className="font-medium text-[#1A1A1A]">{l.full_name}{l.is_secondary && <span className="ml-2 text-[10px] text-amber-700">SECONDARY</span>}</p><p className="text-xs text-neutral-500">{l.company_name || "—"}</p></div>
            <div className="md:col-span-2 font-mono text-xs text-neutral-500">{l.phone_e164}</div>
            <div className="md:col-span-2"><ServiceTag service={l.service_category} /></div>
            <div className="flex flex-wrap gap-1 md:col-span-3"><StageBadge stage={l.lifecycle_stage} /><StatusBadge status={l.lead_status} /></div>
            <div className="md:col-span-2 md:text-right"><p className="font-mono text-sm text-neutral-800">{inr(l.estimated_budget)}</p><p className="text-xs text-neutral-400">{l.assigned_user_name || "Unassigned"} · {fmtTime(l.next_action_at)}</p></div>
          </Link>
        ))}
        {leads.length === 0 && <p data-testid="leads-empty" className="p-8 text-center text-sm text-neutral-400">No leads match.</p>}
      </div>
      <LeadFormDialog open={open} onOpenChange={setOpen} onCreated={refresh} />
    </div>
  );
}
